import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Modal,
  IconButton,
  Button,
  Chip,
  ListItem,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axios from "axios";
import { Header } from "../Header";
import { apiOperation, headers } from "../../services/api";
import { ContentModal, HeaderModal, SubTitle } from "../Login/style";
import { Close, FilterList } from "@mui/icons-material";
import { FilterForm } from "./FilterForm";
import { Error } from "../Error";
import { filters, IFilter } from "./models";
import moment from "moment";

interface ResumoPorteItem {
  categoria: string;
  totalAnalises: number;
  mediaScore: number;
  mediaLimiteReais: string;
  percentualComDocumentos: string;
  tempoFundacaoMedioAnos: number;
}

interface ChipData {
  id: string;
  value: string;
}

// Função para obter filtro padrão dos últimos 30 dias
const getDefaultFilter = (): IFilter => {
  const dataFinal = moment().format("YYYY-MM-DD");
  const dataInicial = moment().subtract(30, "days").format("YYYY-MM-DD");

  return {
    ...filters,
    dataInicial,
    dataFinal,
  };
};

export const ResumoPorPorte = () => {
  const [data, setData] = useState<ResumoPorteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dataFilter, setDataFilter] = useState<IFilter>(getDefaultFilter());
  const [chipDataList, setChipDataList] = useState<ChipData[]>([]);
  const [open, setOpen] = useState(false);
  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });

  // Função para gerar chips iniciais dos últimos 30 dias
  const generateInitialChips = (): ChipData[] => {
    const defaultFilter = getDefaultFilter();
    const chips: ChipData[] = [];

    if (defaultFilter.dataInicial && defaultFilter.dataFinal) {
      chips.push({
        id: "data",
        value: `${moment(defaultFilter.dataInicial).format(
          "DD/MM/YYYY"
        )} - ${moment(defaultFilter.dataFinal).format("DD/MM/YYYY")}`,
      });
    }

    return chips;
  };

  function searchFilter(item: any) {
    const listFilters: ChipData[] = [];

    // Tratamento especial para datas
    if (item.dataInicial && item.dataFinal) {
      listFilters.push({
        id: "data",
        value: `${moment(item.dataInicial).format("DD/MM/YYYY")} - ${moment(
          item.dataFinal
        ).format("DD/MM/YYYY")}`,
      });
    }

    // Outros filtros
    Object.keys(item).forEach((key) => {
      if (item[key] && key !== "dataInicial" && key !== "dataFinal") {
        listFilters.push({
          id: key,
          value: item[key],
        });
      }
    });

    setDataFilter({ ...item });
    setChipDataList(listFilters);
    setOpen(false);
    get(mountParams(item));
  }

  function deleteFilter(item: any) {
    if (item.id.includes("data")) {
      const newChipDataList = chipDataList.filter(
        (chip) => !chip.id.includes("data")
      );
      setChipDataList([...newChipDataList]);
      setDataFilter({ ...dataFilter, dataFinal: "", dataInicial: "" });
      get(); // Recarrega sem filtro de data
    } else {
      const index = chipDataList.findIndex((chip) => chip.id === item.id);
      if (index !== -1) {
        const newChipDataList = [...chipDataList];
        newChipDataList.splice(index, 1);
        setChipDataList(newChipDataList);

        const newDataFilter = { ...dataFilter, [item.id]: "" };
        setDataFilter(newDataFilter);
        get(mountParams(newDataFilter));
      }
    }
  }

  const get = async (params = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const config = {
        headers: headers(),
      };

      const url = `/analise-porte-resumo${params}`;
      const { data: responseData } = await apiOperation.get<{
        valor: ResumoPorteItem[];
      }>(url, config);

      // Verifica se responseData tem a propriedade valor ou se é diretamente um array
      const dataArray = responseData?.valor || responseData;
      setData(Array.isArray(dataArray) ? dataArray : []);
      setError("");
    } catch (err: any) {
      console.error("Erro ao carregar dados:", err);
      setError("Erro ao carregar os dados.");
      setErrorApi({
        message: err?.response?.data?.message || "Erro ao carregar os dados.",
        title: "Erro",
        state: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const mountParams = (filter: IFilter): string => {
    const params = new URLSearchParams();

    if (filter.dataInicial) {
      params.append("dataInicial", filter.dataInicial);
    }
    if (filter.dataFinal) {
      params.append("dataFinal", filter.dataFinal);
    }

    return params.toString() ? `?${params.toString()}` : "";
  };

  useEffect(() => {
    const fetchData = async () => {
      // Inicializa com filtro dos últimos 30 dias
      const defaultFilter = getDefaultFilter();
      const initialChips = generateInitialChips();

      setDataFilter(defaultFilter);
      setChipDataList(initialChips);

      // Carrega dados com filtro inicial
      await get(mountParams(defaultFilter));
    };

    fetchData();
  }, []); // Remove a dependência circular

  if (loading) {
    return (
      <>
        <Header />
        <Box p={4}>
          <Typography>Carregando...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Box p={4}>
          <Typography color="error">{error}</Typography>
        </Box>
      </>
    );
  }

  // Filtra o total geral e os portes (com normalização)
  const totalGeral = data.find(
    (item) =>
      item.categoria && item.categoria.trim().toUpperCase() === "TOTAL GERAL"
  );
  const dataPorPorte = data.filter(
    (item) =>
      item.categoria && item.categoria.trim().toUpperCase() !== "TOTAL GERAL"
  );

  return (
    <>
      <Header />
      <Box p={4}>
        <Typography variant="h4" mb={4} color="primary">
          Resumo de análises de crédito
        </Typography>
        <Box display={"flex"} alignItems="center" mb={2}>
          <Box flexGrow={1}>
            {chipDataList?.map((chipData) => (
              <Chip
                style={{ marginRight: "12px", marginBottom: "8px" }}
                key={chipData.id}
                label={chipData.value}
                color="primary"
                onDelete={() => deleteFilter(chipData)}
              />
            ))}
          </Box>
          <Button
            style={{ minWidth: "180px" }}
            variant="outlined"
            onClick={handleOpen}
            startIcon={<FilterList />}
          >
            Filtrar Dados
          </Button>
        </Box>

        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">Total de Análises</Typography>
                <Typography variant="h5">
                  {totalGeral?.totalAnalises ?? "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">
                  % análises com documento
                </Typography>
                <Typography variant="h5">
                  {totalGeral?.percentualComDocumentos ?? "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">
                  Média Limite Sugerido
                </Typography>
                <Typography variant="h5">
                  {totalGeral?.mediaLimiteReais ?? "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">
                  Tempo Fundação Médio (anos)
                </Typography>
                <Typography variant="h5">
                  {totalGeral?.tempoFundacaoMedioAnos ?? "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">Score Médio</Typography>
                <Typography variant="h5">
                  {totalGeral?.mediaScore !== undefined
                    ? (totalGeral.mediaScore * 10).toLocaleString("pt-BR", {
                        maximumFractionDigits: 2,
                      })
                    : "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Total de Análises por Porte
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dataPorPorte}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="categoria" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="totalAnalises"
                      name="Total de Análises"
                      fill="var(--dv-1)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Score Médio por Porte
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={dataPorPorte.map((item) => ({
                      ...item,
                      mediaScore: parseFloat((item.mediaScore * 10).toFixed(2)),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="categoria" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="mediaScore"
                      name="Score Médio"
                      fill="var(--success)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ContentModal>
          <HeaderModal>
            <SubTitle>Filtrar Dados</SubTitle>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </HeaderModal>
          <Box>
            <FilterForm
              onSave={(event) => searchFilter(event)}
              dataFilter={dataFilter}
            />
          </Box>
        </ContentModal>
      </Modal>

      <Error
        hasOpen={errorApi.state}
        handleClose={() => setErrorApi({ ...errorApi, state: false })}
        errorMsg={errorApi.message}
        title={errorApi.title}
      />
    </>
  );
};

export default ResumoPorPorte;
